import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { confirmBilling, fetchServiceSubscription } from "@/api/service-subscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type ReturnState = "checking" | "reflected" | "stillPending" | "paymentRequired" | "canceled" | "unavailable";

export const BILLING_RETURN_POLL_INTERVAL_MS = 2_000;
const MAX_POLLS = 10;
// Ask the server to read Stripe directly once the webhook seems late.
const CONFIRM_AFTER_POLLS = 3;

export function BillingReturnPage() {
  const { t } = useTranslation("admin");
  const [searchParams] = useSearchParams();
  const returnedFromCheckout = searchParams.get("status") === "success";
  const [state, setState] = useState<ReturnState>(returnedFromCheckout ? "checking" : "canceled");

  useEffect(() => {
    if (!returnedFromCheckout) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;

    const poll = async () => {
      polls += 1;
      try {
        if (polls === CONFIRM_AFTER_POLLS + 1) {
          await confirmBilling().catch(() => undefined);
        }
        const current = await fetchServiceSubscription();
        if (stopped) return;
        if (!current) {
          setState("unavailable");
          return;
        }
        if (!current.checkoutPending) {
          setState(current.commercialState === "payment_required" ? "paymentRequired" : "reflected");
          return;
        }
      } catch {
        if (stopped) return;
        setState("unavailable");
        return;
      }
      if (polls >= MAX_POLLS) {
        setState("stillPending");
        return;
      }
      timer = setTimeout(() => void poll(), BILLING_RETURN_POLL_INTERVAL_MS);
    };
    void poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [returnedFromCheckout]);

  const tone = state === "reflected"
    ? "bg-emerald-50 text-emerald-900"
    : state === "checking" || state === "canceled"
      ? "bg-sky-50 text-sky-900"
      : "bg-amber-50 text-amber-900";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.14),_transparent_45%),linear-gradient(180deg,_#f7fcff_0%,_#ffffff_62%)] px-4">
      <Card className="w-full max-w-xl border-sky-200 shadow-xl">
        <CardHeader>
          <CardTitle className="text-2xl">{t("billingReturn.title")}</CardTitle>
          <CardDescription>{t("billingReturn.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div role="status" className={`rounded-lg p-4 text-sm ${tone}`}>
            {t(`billingReturn.${state}`)}
          </div>
          <div className="flex gap-3">
            <Button asChild>
              <Link to="/console/service-subscription">{t("billingReturn.backToServiceSubscription")}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/console/dashboard">{t("billingReturn.backToDashboard")}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
